import jsPDF from 'jspdf';

const strategyMap = {
  conservative: 'Conservative',
  moderate_conservative: 'Moderate Conservative',
  moderate: 'Moderate',
  moderate_aggressive: 'Moderate Aggressive',
  aggressive: 'Aggressive'
};

const purposeLabels = {
  emergency_fund: 'Emergency fund',
  short_term_savings: 'Short-term savings',
  specific_purchase: 'Saving for a specific purchase',
  retirement: 'Retirement planning',
  tax_free_savings: 'Tax-free savings',
  wealth_diversification: 'Wealth diversification',
  income_generation: 'Income generation',
  education: 'Education funding',
  estate_planning: 'Estate planning',
  offshore_diversification: 'Offshore diversification',
  other: 'Other'
};

const timeHorizonLabels = {
  less_than_1_year: 'Less than 1 year',
  '1_to_3_years': '1 to 3 years',
  '3_to_5_years': '3 to 5 years',
  '5_to_10_years': '5 to 10 years',
  more_than_10_years: 'More than 10 years',
  unsure: 'Unsure'
};

const liquidityLabels = {
  immediate_access: 'Yes, I may need access at short notice',
  partial_access: 'Yes, but only a portion',
  no_access_needed: 'No, I do not expect to need access soon',
  unsure: 'I am unsure'
};

const withdrawalLabels = {
  no_withdrawals: 'No withdrawals planned',
  once_off: 'Once-off withdrawal expected',
  regular_monthly: 'Regular monthly withdrawals',
  irregular: 'Irregular withdrawals may be needed',
  unsure: 'Unsure'
};

const investmentLabels = {
  first_time: 'First time investing',
  have_investments: 'Already have investments',
  retirement_only: 'Retirement savings only',
  discretionary_only: 'Discretionary investments only',
  both: 'Both retirement and discretionary',
  unsure: 'I am unsure'
};

const experienceLabels = {
  no_knowledge: 'No knowledge about investing',
  some_research: 'Done some research',
  some_experience: 'Some experience with investing',
  experienced: 'Experienced investor',
  highly_experienced: 'Highly experienced investor'
};

const riskProfileLabels = {
  conservative: 'Conservative',
  moderate_conservative: 'Moderate Conservative',
  moderate: 'Moderate',
  moderate_aggressive: 'Moderate Aggressive',
  aggressive: 'Aggressive',
  unsure: 'I do not know'
};

const questionLabels = {
  purpose: 'What is the main purpose of this investment?',
  time_horizon: 'How long do you plan to keep this money invested?',
  liquidity_need: 'Will you need access to this money in the near future?',
  expected_withdrawals: 'Do you plan to make withdrawals from this investment?',
  current_investments: 'Do you currently have any investments?',
  experience: 'How would you describe your investment experience?',
  stated_risk_profile: 'What risk profile do you consider yourself to be?'
};

const answerLabelMaps = {
  purpose: purposeLabels,
  time_horizon: timeHorizonLabels,
  liquidity_need: liquidityLabels,
  expected_withdrawals: withdrawalLabels,
  current_investments: investmentLabels,
  experience: experienceLabels,
  stated_risk_profile: riskProfileLabels
};

const scaleLabels = {
  1: 'Strongly Disagree',
  2: 'Disagree',
  3: 'Slightly Disagree',
  4: 'Slightly Agree',
  5: 'Agree',
  6: 'Strongly Agree'
};

export function generateComplianceReport({ assessment, strategies, personalityProfile }) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let y = margin;

  const client = assessment.client || {};
  const answers = assessment.investmentAnswers || [];
  const riskScore = assessment.riskScore || {};
  const recommendation = assessment.recommendation || {};
  const strategy = recommendation.strategy || {};

  // Helper: add page footer
  const addFooter = (pageNum, totalPages) => {
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('RDA Investment Compliance Report — Confidential', margin, pageHeight - 10);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  };

  // Helper: check page overflow and add new page if needed
  const checkPage = (needed) => {
    if (y + needed > pageHeight - 20) {
      doc.addPage();
      y = margin;
    }
  };

  // ===== HEADER =====
  doc.setFillColor(30, 58, 138);
  doc.rect(0, 0, pageWidth, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Investment Compliance Report', margin, 18);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('RDA Financial Services', margin, 28);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, margin, 35);

  y = 50;

  // ===== SECTION 1: CLIENT DETAILS =====
  doc.setTextColor(30, 58, 138);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('1. Client Details', margin, y);
  y += 2;
  doc.setDrawColor(30, 58, 138);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);

  const clientFields = [
    ['Full Name', client.full_name || '-'],
    ['Date of Birth', client.date_of_birth ? (() => {
      const dob = new Date(client.date_of_birth);
      const today = new Date();
      let calcAge = today.getFullYear() - dob.getFullYear();
      const md = today.getMonth() - dob.getMonth();
      if (md < 0 || (md === 0 && today.getDate() < dob.getDate())) calcAge--;
      return `${dob.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })} (${calcAge} years)`;
    })() : '-'],
    ['Email', client.email || '-'],
    ['Cellphone', client.cellphone || '-'],
    ['Assessment Date', assessment.created_at ? new Date(assessment.created_at).toLocaleDateString('en-ZA') : '-'],
    ['Assessment Status', (assessment.assessment_status || '-').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())],
    ['Advisor Override', assessment.advisor_override ? 'Yes' : 'No']
  ];

  clientFields.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(value), margin + 50, y);
    y += 6;
  });

  y += 6;

  // ===== SECTION 2: INVESTMENT QUESTIONNAIRE =====
  checkPage(30);
  doc.setTextColor(30, 58, 138);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('2. Investment Questionnaire', margin, y);
  y += 2;
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);

  answers.forEach((answer) => {
    checkPage(14);

    const questionText = questionLabels[answer.question_key] || answer.question_key;
    const labelMap = answerLabelMaps[answer.question_key] || {};
    const answerText = labelMap[answer.answer_value] || answer.answer_value;

    doc.setFont('helvetica', 'bold');
    doc.text(`Q: ${questionText}`, margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 58, 138);
    doc.text(`A: ${answerText}`, margin + 5, y);
    doc.setTextColor(60, 60, 60);
    y += 8;
  });

  y += 4;

  // ===== SECTION 3: PERSONALITY ASSESSMENT =====
  checkPage(30);
  doc.setTextColor(30, 58, 138);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('3. Financial Personality Assessment', margin, y);
  y += 2;
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  // Personality profile summary
  if (personalityProfile && personalityProfile.length > 0) {
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);

    personalityProfile.forEach((metric) => {
      checkPage(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${metric.name}:`, margin, y);
      doc.setFont('helvetica', 'normal');
      doc.text(`${metric.level}`, margin + 50, y);
      y += 5;
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      const interpLines = doc.splitTextToSize(metric.interpretation || '', contentWidth - 5);
      doc.text(interpLines, margin + 5, y);
      y += interpLines.length * 4 + 4;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
    });

    y += 4;

    // Overall risk category
    checkPage(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Overall Risk Category:', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.text(strategyMap[riskScore.risk_category] || riskScore.risk_category || '-', margin + 50, y);
    y += 10;
  }

  // Personality answers detail (from DB)
  const personalityAnswers = assessment.personalityAnswers;
  if (personalityAnswers && personalityAnswers.length > 0) {
    checkPage(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Detailed Personality Answers:', margin, y);
    y += 8;

    const sectionTitles = {
      gain_attraction: 'Gain Attraction',
      loss_aversion: 'Loss Aversion',
      volatility_anxiety: 'Volatility Anxiety',
      intervention_risk: 'Intervention Risk'
    };

    // Build lookup: question_id -> answer from client's responses
    const answerMap = {};
    personalityAnswers.forEach(a => { answerMap[a.question_id] = a.answer_value; });

    // Group all questions by metric_mapping from the database
    const grouped = {};
    (assessment.personalityQuestions || []).forEach(q => {
      const section = q.metric_mapping;
      if (!grouped[section]) grouped[section] = [];
      grouped[section].push(q);
    });

    // Render each section
    Object.keys(sectionTitles).forEach(sectionKey => {
      const sectionQuestions = grouped[sectionKey];
      if (!sectionQuestions || sectionQuestions.length === 0) return;

      checkPage(16);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 80);
      doc.text(`\u2014 ${sectionTitles[sectionKey]} \u2014`, margin, y);
      y += 6;

      sectionQuestions.forEach((q, idx) => {
        checkPage(10);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(60, 60, 60);
        const qText = q.question_text;
        const wrappedQ = doc.splitTextToSize(`${idx + 1}. ${qText}`, contentWidth - 5);
        doc.text(wrappedQ, margin + 2, y);
        y += wrappedQ.length * 3.5 + 1;

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 58, 138);
        const answerVal = scaleLabels[answerMap[q.id]] || answerMap[q.id] || 'Not answered';
        doc.text(`Answer: ${answerVal}`, margin + 5, y);
        doc.setTextColor(60, 60, 60);
        y += 6;
      });
      y += 3;
    });
    y += 4;
  }
  // ===== SECTION 4: INVESTMENT RECOMMENDATION =====
  checkPage(40);
  doc.setTextColor(30, 58, 138);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('4. Investment Recommendation', margin, y);
  y += 2;
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);

  // Recommended strategy
  if (strategy.strategy_name) {
    doc.setFont('helvetica', 'bold');
    doc.text('Recommended Strategy:', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(strategy.strategy_name, margin + 5, y);
    y += 5;
    doc.text(`Risk Profile: ${strategyMap[strategy.risk_profile] || strategy.risk_profile || '-'}`, margin + 5, y);
    y += 8;
  } else {
    doc.setFont('helvetica', 'italic');
    doc.text('No recommendation generated yet.', margin, y);
    y += 8;
  }

  // Override details
  if (assessment.advisor_override && assessment.override_strategy_id) {
    checkPage(30);

    // Yellow banner
    doc.setFillColor(255, 243, 199);
    doc.rect(margin, y - 2, contentWidth, 7, 'F');
    doc.setTextColor(146, 64, 14);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('ADVISOR OVERRIDE APPLIED', margin + 2, y + 2);
    y += 10;

    doc.setTextColor(60, 60, 60);
    doc.setFontSize(10);

    const overrideStrategy = strategies.find(s => s.id === assessment.override_strategy_id);
    if (overrideStrategy) {
      doc.setFont('helvetica', 'bold');
      doc.text('Override Strategy:', margin, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.text(overrideStrategy.strategy_name, margin + 5, y);
      y += 5;
      doc.text(`Risk Profile: ${strategyMap[overrideStrategy.risk_profile] || '-'}`, margin + 5, y);
      y += 8;
    }

    if (assessment.override_reason) {
      doc.setFont('helvetica', 'bold');
      doc.text('Reason for Override:', margin, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      const reasonLines = doc.splitTextToSize(assessment.override_reason, contentWidth - 5);
      doc.text(reasonLines, margin + 5, y);
      y += reasonLines.length * 5 + 4;
    }

    doc.setFont('helvetica', 'bold');
    doc.text('Client Approved Override:', margin, y);
    doc.setFont('helvetica', 'normal');
    if (assessment.client_approved_override === true) {
      doc.setTextColor(22, 101, 52);
      doc.text('Yes', margin + 55, y);
    } else {
      doc.setTextColor(220, 38, 38);
      doc.text(assessment.client_approved_override === null || assessment.client_approved_override === undefined ? 'Not confirmed' : 'No', margin + 55, y);
    }
    doc.setTextColor(60, 60, 60);
    y += 10;
  }

  // ===== SECTION 5: DECLARATION =====
  checkPage(80);
  doc.setTextColor(30, 58, 138);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('5. Client Declaration', margin, y);
  y += 2;
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const declarations = [
    'I, the undersigned client, hereby confirm and acknowledge the following:',
    '1. I have read and understood the contents of this Investment Compliance Report in its entirety, including the investment questionnaire responses, financial personality assessment results, and the recommended investment strategy.',
    '2. The information provided in the investment questionnaire and personality assessment was completed by me personally and reflects my true and accurate financial position, risk tolerance, and investment preferences at the time of completion.',
    '3. I understand that the recommended investment strategy is based on my responses and that past performance of any investment is not indicative of future results.',
    '4. I acknowledge that all investments carry inherent risks, including the potential loss of capital, and that no guarantee of returns has been made by RDA Financial Services or its representatives.',
    '5. I understand that I may change my investment strategy at any time by contacting my financial advisor, and that any changes will be documented accordingly.',
    '6. I confirm that I have had the opportunity to ask questions regarding the contents of this report and that any questions have been answered to my satisfaction.',
    assessment.advisor_override
          ? '7. I have been informed that an advisor override was applied to the recommended strategy. I understand the reason for this override as documented in this report, and I approve of the change.'
          : '7. No advisor override was applied to the recommendation generated from my responses.',
    '8. I understand that this report will be retained by RDA Financial Services as part of my compliance records in accordance with regulatory requirements under the Financial Advisory and Intermediary Services Act (FAIS Act) and the Financial Sector Regulation Act.'
  ];

  declarations.forEach((text) => {
    checkPage(15);
    const lines = doc.splitTextToSize(text, contentWidth);
    doc.text(lines, margin, y);
    y += lines.length * 5 + 3;
  });

  y += 10;

  // Signature lines
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Client Acknowledgement', margin, y);
  y += 8;
  doc.setFont('helvetica', 'normal');

  doc.line(margin, y, margin + 70, y);
  doc.line(pageWidth - margin - 70, y, pageWidth - margin, y);
  y += 5;
  doc.text('Client Full Name', margin, y);
  doc.text('Date', pageWidth - margin - 70, y);
  y += 10;
  doc.line(margin, y, margin + 70, y);
  y += 5;
  doc.text('Client Signature', margin, y);

  y += 15;
  doc.setFont('helvetica', 'bold');
  doc.text('Advisor Confirmation', margin, y);
  y += 8;
  doc.setFont('helvetica', 'normal');

  doc.line(margin, y, margin + 70, y);
  doc.line(pageWidth - margin - 70, y, pageWidth - margin, y);
  y += 5;
  doc.text('Advisor Name', margin, y);
  doc.text('Date', pageWidth - margin - 70, y);
  y += 10;
  doc.line(margin, y, margin + 70, y);
  y += 5;
  doc.text('Advisor Signature', margin, y);

  // Add footers to all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(i, totalPages);
  }

  // Save
  const filename = `Compliance_Report_${(client.full_name || 'Client').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
}
